import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CloseIconSVG } from 'src/assets/svg';
import { AppModal } from './AppModal';

type FormDialogProps = {
    visible: boolean;
    title: string;
    onClose: () => void;
    children: ReactNode;
};

/**
 * A small dialog with the same header, body and footer layout as the calendar
 * event dialogs. Put a form inside with `DialogBody` and `DialogFooter`.
 */
export const FormDialog = ({
    visible,
    title,
    onClose,
    children,
}: FormDialogProps) => {
    const { t } = useTranslation();
    if (!visible) return null;
    return createPortal(
        <div className="fixed z-100 inset-0 overflow-y-auto bg-black bg-opacity-75 transition-opacity">
            <AppModal show clickOutside={onClose}>
                <div className="absolute top-1/2 left-1/2 flex max-h-[92vh] w-[min(94vw,560px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-30px bg-lightPeach text-woodyBrown shadow-xl">
                    <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-woodyBrown/15 px-5 py-5 sm:px-8">
                        <h2 className="text-xl font-semibold">{title}</h2>
                        <button
                            type="button"
                            aria-label={t('common.close')}
                            title={t('common.close')}
                            onClick={onClose}
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-woodyBrown transition-colors hover:bg-woodyBrown hover:text-lightPeach focus:outline-none"
                        >
                            <CloseIconSVG className="h-3 w-3" />
                        </button>
                    </header>
                    {children}
                </div>
            </AppModal>
        </div>,
        document.body,
    );
};

export const DialogBody = ({ children }: { children: ReactNode }) => (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-6 sm:px-8">
        {children}
    </div>
);

/** Footer actions on the right; `start` holds a secondary action on the left. */
export const DialogFooter = ({
    start,
    children,
}: {
    start?: ReactNode;
    children: ReactNode;
}) => (
    <footer className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-woodyBrown/15 px-5 py-4 sm:px-8">
        <div>{start}</div>
        <div className="flex gap-3">{children}</div>
    </footer>
);
